import api from "./api";
import { mapUserStatus } from "./userService"
import useUserStore from "../stores/userStore";

const waitForState = async () => {
  //used to wait for state since userStore in initialzed on app loading
  return new Promise((resolve) => {
    const checkState = () => {
      const { userId, communityId } = useUserStore.getState();
      if (userId && communityId) {
        resolve({ userId, communityId });
      } else {
        setTimeout(checkState, 100);
      }
    };
    checkState();
  });
};

export const mapMembershipRole = {
  CA: "Community Admin",
  GL: "Group Leader",
  U: "Member",
};

export const membershipDataMap = (item) => ({
  membershipId: item?._id ?? null,
  role: item?.role ?? null,
  status: item?.status ?
    (item?.status === "D" && !item?.entity?.approved) ? "Invitation sent"
      : mapUserStatus[item?.status] !== "undefined" ? mapUserStatus[item?.status]
        : null
    : null,
  profileId: item?.profile?._id ?? null,
  profileName: item?.profile?.name ?? null,
  profileCreateDate: item?.profile?.create_date ?? null,
  profileLastUpdate: item?.profile?.last_update ?? null,
  communityId: item?.community?._id ?? null,
  communityName: item?.community?.name ?? null,
  groupId: item?.group?._id ?? null,
  groupName: item?.group?.name ?? null,
  groupDateCreated: item?.group?.create_date ?? null,
  groupDateUpdated: item?.group?.last_update ?? null,
  groupLeader: item?.group?.leaders?.length
    ? item?.group.leaders.join(", ")
    : null,
  entityId: item?.entity?._id ?? null,
  entityName: item?.entity?.name ?? null,
  entityApproved: item?.entity?.approved ?? null,
  entityStatus: item?.entity?.status ?? null,
  email: item?.entity?.email || null,
  lastLogin: item?.entity?.last_login ?? null,
  createDate: item?.create_date ?? null,
  lastUpdate: item?.last_update ?? null,
});

export const membershipService = {
  getMembersByGroup: async (groupId) => {
    try {
      if (!groupId) {
        throw new Error("GroupId is required to fetch membership data.");
      }

      // console.log("Fetching memberships for GroupId:", groupId);

      const response = await api.get("/mapi/membership/getMembership", {
        params: { GroupId: groupId },
      });

      console.log("API Response (Group Membership):", response.data);

      return response.data; // Return the raw response data
    } catch (error) {
      console.error("Failed to fetch membership data by group:", error);
      return {
        Error: true,
        ErrorDesc: error.response?.data?.ErrorDesc || error.message,
        Data: null,
      };
    }
  },

  /**
   * Fetch membership data by entity ID and profile ID. If entityId is not provided, it will wait for the state to resolve.
   * @param entityId
   * @param profileId
   * @return {Promise<{memberships: {profileName, role, groupId, entityId, membershipId, groupName, profileId, entityName, lastUpdate, communityName, communityId, status, createDate}[]}>}
   */
  getMembershipById: async (entityId, profileId) => {
    try {
      // If entityId is not provided, get it from waitForState()
      // const resolvedEntityId = entityId || (await waitForState()).userId;

      const baseUrl = "/mapi/membership/getMembershipById";

      // Construct params dynamically, only adding ProfileId and EntityId if available
      const params = {
        EntityId: entityId,
        ...(profileId && { ProfileId: profileId }), // Only add ProfileId if provided
      };

      // console.log("API Request Params:", params); // Logs the parameters being sent

      const response = await api.get(baseUrl, { params });

      if (!response.data || response.data.Error) {
        throw new Error("Error fetching membership data");
      }

      return response.data.Data;

      // const membershipData = response.data.Data;
      //
      // if (!Array.isArray(membershipData) || membershipData.length === 0) {
      //   console.warn("No membership data found");
      //   return { memberships: [] };
      // }
      //
      // // Process memberships
      // const memberships = membershipData.map((membership) => ({
      //   membershipId: membership?._id || null,
      //   role: membership?.role || null,
      //   status: membership?.status || null,
      //   profileId: membership?.profile?._id || null,
      //   profileName: membership?.profile?.name || null,
      //   communityId: membership?.community?._id || null,
      //   communityName: membership?.community?.name || null,
      //   groupId: membership?.group?._id || null,
      //   groupName: membership?.group?.name || null,
      //   entityId: membership?.entity?._id || null,
      //   entityName: membership?.entity?.name || null,
      //   createDate: membership?.create_date || null,
      //   lastUpdate: membership?.last_update || null,
      // }));
      //
      // return { memberships };
    } catch (error) {
      console.error("Failed to fetch membership data:", error);
      return { memberships: [] };
    }
  },

  getMembershipByCommunity: async (communityId = null) => {
    try {
      // console.log("Starting getMembershipByCommunity function...");

      if (communityId) {
        // console.log("CommunityId provided:", communityId);
        const response = await api.get("/mapi/membership/getMembership", {
          params: { CommunityId: communityId },
        });

        // console.log("API Response (Group Membership):", response.data);

        return response.data; // Return the raw response data
      } else {
        // communityId not provided, fetching the existing way...

        // Wait until Zustand has valid state
        const { userId, communityId } = await waitForState();

        console.log(
          "Fetched from Zustand -> userId:",
          userId,
          "communityId:",
          communityId
        );

        const response = await api.get("/mapi/membership/getMembership", {
          params: {
            // EntityId: userId,
            CommunityId: communityId,
          },
        });

        console.log("API Response (Community Membership):", response.data);

        if (!response.data || response.data.Error) {
          throw new Error("Error fetching membership data by community");
        }

        return response.data;

        // const membershipData = response.data.Data;
        // if (!Array.isArray(membershipData) || membershipData.length === 0) {
        //   console.warn("No membership data found for community");
        //   return { Error: false, ErrorDesc: null, Data: [] };
        // }
        //
        // const mappedData = membershipData.map(membershipDataMap);
        //
        // console.log("Mapped Data with Group Names:", mappedData);
        //
        // return {
        //   Error: false,
        //   ErrorDesc: null,
        //   Data: mappedData,
        // };
      }
    } catch (error) {
      console.error("Failed to fetch membership data by community:", error);
      return {
        Error: true,
        ErrorDesc: error.response?.data?.ErrorDesc || error.message,
        Data: null,
      };
    }
  },

  editMembershipDetails: async (memberData) => {
    try {
      console.log("Editing Member:", memberData);

      const response = await api.post(
        `/mapi/membership/editMembership`,
        memberData
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error updating membership details:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  removeMembership: async (membershipId) => {
    try {
      console.log("Removing Membership:", membershipId);

      // Get the OperatorId from Zustand state
      const { userId } = await waitForState();

      // Construct payload
      const payload = {
        OperatorId: userId,
        MembershipId: membershipId,
      };

      // API request
      const response = await api.post(
        "/mapi/membership/removeMembership",
        payload
      );

      // Log response
      console.log("Remove Membership Response:", response.data);

      return response.data;
    } catch (error) {
      console.error(
        "Error removing membership:",
        error.response?.data || error.message
      );
      throw error;
    }
  },
};
